import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_ENDPOINTS } from '../config/apiRouting';
import { useAuth } from '../contexts/AuthContext';

interface ModifierGroup {
  enterpriseId: string;
  groupId: string;
  name: string;
  internalName: string;
  required: boolean;
  minSelections: number;
  maxSelections: number | null;
  freeCount: number;
  defaultOptionId: string | null;
  displayStyle: 'BUTTONS' | 'LIST' | 'STEPPER' | 'TOGGLE' | 'TEXT_INPUT';
  kdsDisplay: 'ALWAYS' | 'ONLY_IF_NON_DEFAULT' | 'NEVER';
  kdsHighlight: 'RED' | 'YELLOW' | null;
  scope: 'ITEM' | 'CATEGORY' | 'SUBCATEGORY' | 'GLOBAL';
  appliesTo: string[];
  sortOrder: number;
  voicePrompt?: string | null;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface ModifierOption {
  groupId: string;
  optionId: string;
  name: string;
  shortName: string;
  priceAdjustment: number;
  priceType: 'FIXED' | 'PERCENTAGE' | 'PER_UNIT';
  priceMultipliers?: Record<string, number> | null;
  ingredientId?: string | null;
  ingredientQty?: number | null;
  nestedGroupId?: string | null;
  nestedGroupIds?: string[] | null;
  isDefault: boolean;
  sortOrder: number;
  kdsStation?: string | null;
  voiceAliases?: string[];
  taxCategory?: string | null;
  active: boolean;
}

interface Template {
  templateId: string;
  name: string;
  description: string;
  groupCount: number;
}

interface ModifierManagementProps {
  user: any;
}

const BLANK_GROUP: Partial<ModifierGroup> = {
  name: '',
  internalName: '',
  required: false,
  minSelections: 0,
  maxSelections: null,
  freeCount: 0,
  defaultOptionId: null,
  displayStyle: 'LIST',
  kdsDisplay: 'ALWAYS',
  kdsHighlight: null,
  scope: 'GLOBAL',
  appliesTo: [],
  sortOrder: 0,
  active: true
};

const BLANK_OPTION: Partial<ModifierOption> = {
  name: '',
  shortName: '',
  priceAdjustment: 0,
  priceType: 'FIXED',
  isDefault: false,
  sortOrder: 0,
  voiceAliases: [],
  active: true
};

const ModifierManagement: React.FC<ModifierManagementProps> = ({ user }) => {
  const { getIdToken } = useAuth();
  const enterpriseId: string =
    user?.enterpriseId ||
    user?.attributes?.['custom:enterpriseId'] ||
    '17527771077049luj0w1ek';

  const [groups, setGroups] = useState<ModifierGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [options, setOptions] = useState<ModifierOption[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [groupForm, setGroupForm] = useState<Partial<ModifierGroup>>(BLANK_GROUP);
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [showGroupForm, setShowGroupForm] = useState(false);

  const [optionForm, setOptionForm] = useState<Partial<ModifierOption>>(BLANK_OPTION);
  const [editingOptionId, setEditingOptionId] = useState<string | null>(null);
  const [showOptionForm, setShowOptionForm] = useState(false);

  const authHeaders = useCallback(async (): Promise<HeadersInit> => {
    const token = await getIdToken();
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
  }, [getIdToken]);

  const loadGroups = useCallback(async () => {
    try {
      const r = await fetch(API_ENDPOINTS.modifierGroups(enterpriseId), { headers: await authHeaders() });
      const body = await r.json();
      if (!r.ok || !body.success) throw new Error(body?.message || `Load groups failed (${r.status})`);
      const items: ModifierGroup[] = body.data || [];
      items.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      setGroups(items);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load groups');
    }
  }, [authHeaders, enterpriseId]);

  const loadOptions = useCallback(async (groupId: string) => {
    try {
      const r = await fetch(API_ENDPOINTS.modifierGroupOptions(groupId), { headers: await authHeaders() });
      const body = await r.json();
      if (!r.ok || !body.success) throw new Error(body?.message || `Load options failed (${r.status})`);
      setOptions(body.data || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load options');
    }
  }, [authHeaders]);

  const loadTemplates = useCallback(async () => {
    try {
      const r = await fetch(API_ENDPOINTS.modifierTemplates(), { headers: await authHeaders() });
      const body = await r.json();
      if (r.ok && body.success) setTemplates(body.data || []);
    } catch {
      // templates are optional for the UI; swallow
    }
  }, [authHeaders]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      await Promise.all([loadGroups(), loadTemplates()]);
      setLoading(false);
    })();
  }, [loadGroups, loadTemplates]);

  useEffect(() => {
    if (selectedGroupId) loadOptions(selectedGroupId);
    else setOptions([]);
  }, [selectedGroupId, loadOptions]);

  const selectedGroup = useMemo(
    () => groups.find((g) => g.groupId === selectedGroupId) || null,
    [groups, selectedGroupId]
  );

  // ---- Group CRUD ----
  const openNewGroup = () => {
    setEditingGroupId(null);
    setGroupForm({ ...BLANK_GROUP });
    setShowGroupForm(true);
    setError(null);
  };
  const openEditGroup = (g: ModifierGroup) => {
    setEditingGroupId(g.groupId);
    setGroupForm({ ...g });
    setShowGroupForm(true);
    setError(null);
  };
  const closeGroupForm = () => {
    setShowGroupForm(false);
    setEditingGroupId(null);
    setGroupForm({ ...BLANK_GROUP });
  };

  const saveGroup = async () => {
    if (!groupForm.name?.trim()) {
      setError('Group name is required');
      return;
    }
    try {
      const payload: any = {
        ...groupForm,
        enterpriseId,
        appliesTo: Array.isArray(groupForm.appliesTo) ? groupForm.appliesTo : []
      };
      const url = editingGroupId
        ? API_ENDPOINTS.modifierGroup(editingGroupId)
        : API_ENDPOINTS.modifierGroupsCreate();
      const method = editingGroupId ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers: await authHeaders(), body: JSON.stringify(payload) });
      const body = await r.json();
      if (!r.ok || !body.success) throw new Error(body?.message || `Save failed (${r.status})`);
      setMessage(editingGroupId ? 'Group updated' : 'Group created');
      closeGroupForm();
      await loadGroups();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    }
  };

  const deleteGroup = async (g: ModifierGroup) => {
    if (!window.confirm(`Deactivate "${g.name}"? It will be hidden from POS but preserved for history.`)) return;
    try {
      const r = await fetch(API_ENDPOINTS.modifierGroup(g.groupId), { method: 'DELETE', headers: await authHeaders() });
      const body = await r.json();
      if (!r.ok || !body.success) throw new Error(body?.message || `Delete failed (${r.status})`);
      setMessage('Group deactivated');
      if (selectedGroupId === g.groupId) setSelectedGroupId(null);
      await loadGroups();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  // ---- Option CRUD ----
  const openNewOption = () => {
    if (!selectedGroupId) return;
    setEditingOptionId(null);
    setOptionForm({ ...BLANK_OPTION, sortOrder: options.length });
    setShowOptionForm(true);
    setError(null);
  };
  const openEditOption = (o: ModifierOption) => {
    setEditingOptionId(o.optionId);
    setOptionForm({ ...o });
    setShowOptionForm(true);
    setError(null);
  };
  const closeOptionForm = () => {
    setShowOptionForm(false);
    setEditingOptionId(null);
    setOptionForm({ ...BLANK_OPTION });
  };

  const saveOption = async () => {
    if (!selectedGroupId) return;
    if (!optionForm.name?.trim()) {
      setError('Option name is required');
      return;
    }
    try {
      if (editingOptionId) {
        const r = await fetch(API_ENDPOINTS.modifierOption(editingOptionId), {
          method: 'PUT',
          headers: await authHeaders(),
          body: JSON.stringify({ ...optionForm, groupId: selectedGroupId })
        });
        const body = await r.json();
        if (!r.ok || !body.success) throw new Error(body?.message || `Update failed (${r.status})`);
        setMessage('Option updated');
      } else {
        const r = await fetch(API_ENDPOINTS.modifierGroupOptions(selectedGroupId), {
          method: 'POST',
          headers: await authHeaders(),
          body: JSON.stringify(optionForm)
        });
        const body = await r.json();
        if (!r.ok || !body.success) throw new Error(body?.message || `Create failed (${r.status})`);
        setMessage('Option created');
      }
      closeOptionForm();
      await loadOptions(selectedGroupId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    }
  };

  const deleteOption = async (o: ModifierOption) => {
    if (!selectedGroupId) return;
    if (!window.confirm(`Deactivate option "${o.name}"?`)) return;
    try {
      const r = await fetch(API_ENDPOINTS.modifierOption(o.optionId, selectedGroupId), {
        method: 'DELETE',
        headers: await authHeaders()
      });
      const body = await r.json();
      if (!r.ok || !body.success) throw new Error(body?.message || `Delete failed (${r.status})`);
      setMessage('Option deactivated');
      await loadOptions(selectedGroupId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  const reorderOptions = async (newOrder: ModifierOption[]) => {
    if (!selectedGroupId) return;
    try {
      await fetch(API_ENDPOINTS.modifierGroupOptionsReorder(selectedGroupId), {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify(newOrder.map((o, i) => ({ optionId: o.optionId, sortOrder: i })))
      });
      await loadOptions(selectedGroupId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Reorder failed');
    }
  };

  const moveOption = (idx: number, dir: -1 | 1) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= options.length) return;
    const next = [...options];
    const [moved] = next.splice(idx, 1);
    next.splice(newIdx, 0, moved);
    setOptions(next);
    reorderOptions(next);
  };

  // ---- Templates ----
  const importTemplate = async (templateId: string) => {
    if (!window.confirm(`Import the "${templateId}" template? Groups + options will be created under this enterprise.`)) return;
    try {
      const r = await fetch(API_ENDPOINTS.modifierTemplateImport(templateId), {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({ enterpriseId })
      });
      const body = await r.json();
      if (!r.ok || !body.success) throw new Error(body?.message || `Import failed (${r.status})`);
      const count = (body.data?.groups || []).length;
      setMessage(`Imported ${count} groups from "${templateId}"`);
      await loadGroups();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Template import failed');
    }
  };

  if (loading && groups.length === 0) {
    return <div style={{ padding: '2rem' }}>Loading modifiers…</div>;
  }

  return (
    <div style={{ padding: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <h1 style={{ margin: 0 }}>Universal Modifiers</h1>
          <div style={{ color: '#666', fontSize: '0.9rem' }}>Enterprise: {enterpriseId}</div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={openNewGroup} style={{ padding: '0.5rem 1rem', background: '#0369a1', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
            + New Group
          </button>
        </div>
      </div>

      {error && (
        <div role="alert" style={{ padding: '0.75rem 1rem', marginBottom: '1rem', background: '#fee', color: '#900', borderRadius: 4 }}>
          {error}
        </div>
      )}
      {message && (
        <div style={{ padding: '0.75rem 1rem', marginBottom: '1rem', background: '#dfd', color: '#060', borderRadius: 4 }}>
          {message}
        </div>
      )}

      {templates.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ margin: '0 0 0.5rem' }}>Industry Templates</h3>
          <p style={{ color: '#666', fontSize: '0.9rem', marginTop: 0 }}>One-click import a preset set of groups + options for a segment.</p>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {templates.map((t) => (
              <button
                key={t.templateId}
                onClick={() => importTemplate(t.templateId)}
                style={{ padding: '0.5rem 0.75rem', border: '1px solid #ccc', background: 'white', borderRadius: 4, cursor: 'pointer', textAlign: 'left' }}
                title={t.description}
              >
                <div style={{ fontWeight: 600 }}>{t.name}</div>
                <div style={{ fontSize: '0.8rem', color: '#666' }}>{t.groupCount} groups</div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '380px 1fr', gap: '1rem' }}>
        <div style={{ border: '1px solid #ddd', borderRadius: 6, padding: '0.75rem' }}>
          <h2 style={{ margin: '0 0 0.75rem', fontSize: '1.1rem' }}>Groups ({groups.length})</h2>
          {groups.length === 0 ? (
            <p style={{ color: '#666' }}>No groups yet. Create one or import a template.</p>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {groups.map((g) => (
                <li
                  key={g.groupId}
                  style={{
                    padding: '0.5rem 0.75rem',
                    borderBottom: '1px solid #eee',
                    cursor: 'pointer',
                    background: selectedGroupId === g.groupId ? '#eef5ff' : 'transparent'
                  }}
                  onClick={() => setSelectedGroupId(g.groupId)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{g.name}</div>
                      <div style={{ fontSize: '0.8rem', color: '#666' }}>
                        {g.required ? 'required' : 'optional'} · {g.minSelections}-{g.maxSelections ?? '∞'} · {g.scope}
                        {g.freeCount ? ` · freeCount ${g.freeCount}` : ''}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                      <button onClick={(e) => { e.stopPropagation(); openEditGroup(g); }} style={{ fontSize: '0.8rem' }}>Edit</button>
                      <button onClick={(e) => { e.stopPropagation(); deleteGroup(g); }} style={{ fontSize: '0.8rem', color: '#900' }}>×</button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div style={{ border: '1px solid #ddd', borderRadius: 6, padding: '0.75rem' }}>
          {!selectedGroup ? (
            <div style={{ color: '#666', padding: '1rem' }}>Select a group on the left to see its options.</div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h2 style={{ margin: 0, fontSize: '1.1rem' }}>{selectedGroup.name} — options ({options.length})</h2>
                <button onClick={openNewOption} style={{ padding: '0.4rem 0.8rem', background: '#0369a1', color: 'white', border: 'none', borderRadius: 4 }}>
                  + New Option
                </button>
              </div>
              {options.length === 0 ? (
                <p style={{ color: '#666' }}>No options yet.</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                      <th style={{ padding: '0.4rem' }}>Order</th>
                      <th style={{ padding: '0.4rem' }}>Name</th>
                      <th style={{ padding: '0.4rem' }}>Short</th>
                      <th style={{ padding: '0.4rem' }}>Price</th>
                      <th style={{ padding: '0.4rem' }}>Type</th>
                      <th style={{ padding: '0.4rem' }}>Default</th>
                      <th style={{ padding: '0.4rem' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {options.map((o, idx) => (
                      <tr key={o.optionId} style={{ borderBottom: '1px solid #eee', opacity: o.active === false ? 0.5 : 1 }}>
                        <td style={{ padding: '0.4rem' }}>
                          <button onClick={() => moveOption(idx, -1)} disabled={idx === 0} style={{ padding: '0 0.25rem' }}>↑</button>
                          <button onClick={() => moveOption(idx, 1)} disabled={idx === options.length - 1} style={{ padding: '0 0.25rem' }}>↓</button>
                        </td>
                        <td style={{ padding: '0.4rem' }}>{o.name}</td>
                        <td style={{ padding: '0.4rem' }}>{o.shortName}</td>
                        <td style={{ padding: '0.4rem' }}>{o.priceAdjustment != null ? (o.priceAdjustment >= 0 ? '+' : '') + o.priceAdjustment.toFixed(2) : '-'}</td>
                        <td style={{ padding: '0.4rem' }}>{o.priceType}</td>
                        <td style={{ padding: '0.4rem' }}>{o.isDefault ? '✓' : ''}</td>
                        <td style={{ padding: '0.4rem' }}>
                          <button onClick={() => openEditOption(o)} style={{ marginRight: '0.25rem' }}>Edit</button>
                          <button onClick={() => deleteOption(o)} style={{ color: '#900' }}>×</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      </div>

      {showGroupForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={closeGroupForm}>
          <div style={{ background: 'white', borderRadius: 8, padding: '1.25rem', width: 'min(720px, 92vw)', maxHeight: '88vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginTop: 0 }}>{editingGroupId ? 'Edit Group' : 'New Group'}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <label>Name*<input type="text" value={groupForm.name || ''} onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} style={{ width: '100%', padding: '0.4rem' }} /></label>
              <label>Internal name<input type="text" value={groupForm.internalName || ''} onChange={(e) => setGroupForm({ ...groupForm, internalName: e.target.value })} style={{ width: '100%', padding: '0.4rem' }} /></label>
              <label>Required<select value={String(!!groupForm.required)} onChange={(e) => setGroupForm({ ...groupForm, required: e.target.value === 'true' })} style={{ padding: '0.4rem' }}><option value="false">No</option><option value="true">Yes</option></select></label>
              <label>Display style<select value={groupForm.displayStyle || 'LIST'} onChange={(e) => setGroupForm({ ...groupForm, displayStyle: e.target.value as any })} style={{ padding: '0.4rem' }}>{['BUTTONS', 'LIST', 'STEPPER', 'TOGGLE', 'TEXT_INPUT'].map((s) => <option key={s} value={s}>{s}</option>)}</select></label>
              <label>Min selections<input type="number" min={0} value={groupForm.minSelections ?? 0} onChange={(e) => setGroupForm({ ...groupForm, minSelections: Number(e.target.value) })} style={{ width: '100%', padding: '0.4rem' }} /></label>
              <label>Max selections (blank = unlimited)<input type="number" min={0} value={groupForm.maxSelections ?? ''} onChange={(e) => setGroupForm({ ...groupForm, maxSelections: e.target.value === '' ? null : Number(e.target.value) })} style={{ width: '100%', padding: '0.4rem' }} /></label>
              <label>Free count<input type="number" min={0} value={groupForm.freeCount ?? 0} onChange={(e) => setGroupForm({ ...groupForm, freeCount: Number(e.target.value) })} style={{ width: '100%', padding: '0.4rem' }} /></label>
              <label>Sort order<input type="number" value={groupForm.sortOrder ?? 0} onChange={(e) => setGroupForm({ ...groupForm, sortOrder: Number(e.target.value) })} style={{ width: '100%', padding: '0.4rem' }} /></label>
              <label>KDS display<select value={groupForm.kdsDisplay || 'ALWAYS'} onChange={(e) => setGroupForm({ ...groupForm, kdsDisplay: e.target.value as any })} style={{ padding: '0.4rem' }}>{['ALWAYS', 'ONLY_IF_NON_DEFAULT', 'NEVER'].map((s) => <option key={s} value={s}>{s}</option>)}</select></label>
              <label>KDS highlight<select value={groupForm.kdsHighlight || ''} onChange={(e) => setGroupForm({ ...groupForm, kdsHighlight: (e.target.value || null) as any })} style={{ padding: '0.4rem' }}><option value="">(none)</option><option value="RED">RED</option><option value="YELLOW">YELLOW</option></select></label>
              <label>Scope<select value={groupForm.scope || 'GLOBAL'} onChange={(e) => setGroupForm({ ...groupForm, scope: e.target.value as any })} style={{ padding: '0.4rem' }}>{['ITEM', 'CATEGORY', 'SUBCATEGORY', 'GLOBAL'].map((s) => <option key={s} value={s}>{s}</option>)}</select></label>
              <label>Applies to (comma-separated IDs)<input type="text" value={(groupForm.appliesTo || []).join(', ')} onChange={(e) => setGroupForm({ ...groupForm, appliesTo: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} style={{ width: '100%', padding: '0.4rem' }} /></label>
            </div>
            <label style={{ display: 'block', marginTop: '0.75rem' }}>Voice prompt<input type="text" value={groupForm.voicePrompt || ''} onChange={(e) => setGroupForm({ ...groupForm, voicePrompt: e.target.value })} style={{ width: '100%', padding: '0.4rem' }} placeholder="e.g., How would you like that cooked?" /></label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
              <button onClick={closeGroupForm}>Cancel</button>
              <button onClick={saveGroup} style={{ background: '#0369a1', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: 4 }}>
                {editingGroupId ? 'Save changes' : 'Create group'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showOptionForm && selectedGroup && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={closeOptionForm}>
          <div style={{ background: 'white', borderRadius: 8, padding: '1.25rem', width: 'min(620px, 92vw)', maxHeight: '88vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginTop: 0 }}>{editingOptionId ? 'Edit Option' : 'New Option'}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <label>Name*<input type="text" value={optionForm.name || ''} onChange={(e) => setOptionForm({ ...optionForm, name: e.target.value })} style={{ width: '100%', padding: '0.4rem' }} /></label>
              <label>Short name<input type="text" value={optionForm.shortName || ''} onChange={(e) => setOptionForm({ ...optionForm, shortName: e.target.value })} style={{ width: '100%', padding: '0.4rem' }} /></label>
              <label>Price adjustment<input type="number" step="0.01" value={optionForm.priceAdjustment ?? 0} onChange={(e) => setOptionForm({ ...optionForm, priceAdjustment: Number(e.target.value) })} style={{ width: '100%', padding: '0.4rem' }} /></label>
              <label>Price type<select value={optionForm.priceType || 'FIXED'} onChange={(e) => setOptionForm({ ...optionForm, priceType: e.target.value as any })} style={{ padding: '0.4rem' }}>{['FIXED', 'PERCENTAGE', 'PER_UNIT'].map((s) => <option key={s} value={s}>{s}</option>)}</select></label>
              <label>Default?<select value={String(!!optionForm.isDefault)} onChange={(e) => setOptionForm({ ...optionForm, isDefault: e.target.value === 'true' })} style={{ padding: '0.4rem' }}><option value="false">No</option><option value="true">Yes</option></select></label>
              <label>KDS station<input type="text" value={optionForm.kdsStation || ''} onChange={(e) => setOptionForm({ ...optionForm, kdsStation: e.target.value })} style={{ width: '100%', padding: '0.4rem' }} /></label>
              <label>Nested group ID<input type="text" value={optionForm.nestedGroupId || ''} onChange={(e) => setOptionForm({ ...optionForm, nestedGroupId: e.target.value })} style={{ width: '100%', padding: '0.4rem' }} placeholder="MODGROUP#..." /></label>
              <label>Tax category<input type="text" value={optionForm.taxCategory || ''} onChange={(e) => setOptionForm({ ...optionForm, taxCategory: e.target.value })} style={{ width: '100%', padding: '0.4rem' }} /></label>
              <label>Ingredient ID<input type="text" value={optionForm.ingredientId || ''} onChange={(e) => setOptionForm({ ...optionForm, ingredientId: e.target.value })} style={{ width: '100%', padding: '0.4rem' }} /></label>
              <label>Ingredient qty<input type="number" step="0.01" value={optionForm.ingredientQty ?? ''} onChange={(e) => setOptionForm({ ...optionForm, ingredientQty: e.target.value === '' ? null : Number(e.target.value) })} style={{ width: '100%', padding: '0.4rem' }} /></label>
            </div>
            <label style={{ display: 'block', marginTop: '0.75rem' }}>Voice aliases (comma-separated)<input type="text" value={(optionForm.voiceAliases || []).join(', ')} onChange={(e) => setOptionForm({ ...optionForm, voiceAliases: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} style={{ width: '100%', padding: '0.4rem' }} placeholder="e.g., black and blue, blackened" /></label>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
              <button onClick={closeOptionForm}>Cancel</button>
              <button onClick={saveOption} style={{ background: '#0369a1', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: 4 }}>
                {editingOptionId ? 'Save changes' : 'Create option'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModifierManagement;
